/**
 * Live Boat — role → SignalK path mapping.
 * v0: Victron GX template (MultiPlus + MPPT + shunt). Vessel 1 / Time N Hand paths.
 * Widget resolution still uses live → DB → demo via useSignalkDashboardData.
 */

export const LIVE_BOAT_TEMPLATE_ID = 'victron-gx-multiplus';

/** @typedef {{ label: string, icon?: string, paths: Record<string, string> }} LiveBoatRoleDef */

export const LIVE_BOAT_ROLES = {
  shore: {
    label: 'Shore',
    icon: 'shore',
    paths: {
      power: 'electrical.inverters.276.acin.power',
      voltage: 'electrical.inverters.276.acin.voltage',
      current: 'electrical.inverters.276.acin.current',
      frequency: 'electrical.inverters.276.acin.frequency',
      source: 'electrical.venus.acSource',
    },
  },
  acLoads: {
    label: 'AC loads',
    icon: 'ac',
    paths: {
      power: 'electrical.inverters.276.acout.power',
      voltage: 'electrical.inverters.276.acout.voltage',
      current: 'electrical.inverters.276.acout.current',
      frequency: 'electrical.inverters.276.acout.frequency',
      totalPower: 'electrical.ac.1.total.realPower',
    },
  },
  houseBattery: {
    label: 'House battery',
    icon: 'battery',
    paths: {
      soc: 'electrical.batteries.279.capacity.stateOfCharge',
      voltage: 'electrical.batteries.279.voltage',
      current: 'electrical.batteries.279.current',
      power: 'electrical.batteries.279.power',
      timeRemaining: 'electrical.batteries.279.capacity.timeRemaining',
      name: 'electrical.batteries.279.name',
    },
  },
  starterBattery: {
    label: 'Starter',
    icon: 'battery',
    paths: {
      voltage: 'electrical.batteries.279-second.voltage',
    },
  },
  solar: {
    label: 'PV charger',
    icon: 'solar',
    paths: {
      panelPower: 'electrical.solar.278.panelPower',
      panelVoltage: 'electrical.solar.278.panelVoltage',
      current: 'electrical.solar.278.current',
      voltage: 'electrical.solar.278.voltage',
      controllerMode: 'electrical.solar.278.controllerMode',
      yieldToday: 'electrical.solar.278.yieldToday',
    },
  },
  inverter: {
    label: 'Inverter',
    icon: 'inverter',
    paths: {
      mode: 'electrical.inverters.276.inverterMode',
      chargerMode: 'electrical.chargers.276.chargingMode',
      voltage: 'electrical.chargers.276.voltage',
      power: 'electrical.chargers.276.power',
      current: 'electrical.chargers.276.current',
      currentLimit: 'electrical.inverters.276.acin.currentLimit',
      vebusDc: 'electrical.venus.vebusDcPower',
    },
  },
  dcLoads: {
    label: 'DC loads',
    icon: 'dc',
    paths: {
      power: 'electrical.venus.dcPower',
      panelPower: 'electrical.venus.totalPanelPower',
    },
  },
  enginePort: {
    label: 'Engine port',
    icon: 'engine',
    paths: {
      rpm: 'propulsion.port.revolutions',
      temperature: 'propulsion.port.temperature',
      runTime: 'propulsion.port.runTime',
      fuelRate: 'propulsion.port.fuel.rate',
    },
  },
  engineStarboard: {
    label: 'Engine stbd',
    icon: 'engine',
    paths: {
      rpm: 'propulsion.starboard.revolutions',
      temperature: 'propulsion.starboard.temperature',
      runTime: 'propulsion.starboard.runTime',
      fuelRate: 'propulsion.starboard.fuel.rate',
    },
  },
  freshWater: {
    label: 'Fresh water',
    icon: 'tank',
    paths: {
      level: 'tanks.freshWater.20.currentLevel',
      remaining: 'tanks.freshWater.20.remaining',
      capacity: 'tanks.freshWater.20.capacity',
    },
  },
  wasteWater: {
    label: 'Waste water',
    icon: 'tank',
    paths: {
      level: 'tanks.wasteWater.21.currentLevel',
      remaining: 'tanks.wasteWater.21.remaining',
      capacity: 'tanks.wasteWater.21.capacity',
    },
  },
  navigation: {
    label: 'Navigation',
    icon: 'nav',
    paths: {
      sog: 'navigation.speedOverGround',
      heading: 'navigation.headingTrue',
      depth: 'environment.depth.belowTransducer',
      waterTemp: 'environment.water.temperature',
    },
  },
};

/** Edges for flow animation: from → to role, power path key on source */
export const LIVE_BOAT_FLOW_EDGES = [
  { from: 'shore', to: 'houseBattery', powerKey: 'power', label: 'shore' },
  { from: 'solar', to: 'houseBattery', powerKey: 'panelPower', label: 'solar' },
  { from: 'houseBattery', to: 'acLoads', powerKey: 'power', label: 'ac' },
  { from: 'houseBattery', to: 'dcLoads', powerKey: 'power', label: 'dc' },
];

export const LIVE_BOAT_NODE_LAYOUT = {
  shore: { x: 12, y: 18, w: 22 },
  acLoads: { x: 66, y: 18, w: 22 },
  houseBattery: { x: 34, y: 38, w: 32 },
  solar: { x: 12, y: 58, w: 22 },
  dcLoads: { x: 66, y: 58, w: 22 },
  enginePort: { x: 8, y: 82, w: 20 },
  engineStarboard: { x: 30, y: 82, w: 20 },
  freshWater: { x: 72, y: 78, w: 24 },
  wasteWater: { x: 72, y: 88, w: 24 },
};
